import { useEffect, useRef, useState, useCallback } from 'react';
import { db } from '../firebaseConfig';
import { collection, addDoc, onSnapshot, query, orderBy, limit, serverTimestamp, deleteDoc, getDocs } from 'firebase/firestore';

// Canvas action types
export type CanvasActionType = 'draw' | 'fill' | 'erase' | 'clear' | 'undo';

export interface DrawAction {
  id: string;
  type: CanvasActionType;
  userId: string;
  userName: string;
  timestamp: any;
  // For draw/erase
  points?: { x: number; y: number }[];
  color?: string;
  lineWidth?: number;
  // For fill
  fillX?: number;
  fillY?: number;
  fillColor?: string;
  // For undo
  undoActionId?: string;
}

interface UseRealtimeCanvasProps {
  canvasId: string;
  userId: string;
  userName: string;
  width: number;
  height: number;
}

export const useRealtimeCanvas = ({ canvasId, userId, userName, width, height }: UseRealtimeCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentTool, setCurrentTool] = useState<'draw' | 'erase' | 'fill'>('draw');
  const [currentColor, setCurrentColor] = useState('#000000');
  const [lineWidth, setLineWidth] = useState(3);
  const [currentPath, setCurrentPath] = useState<{ x: number; y: number }[]>([]);
  const [actions, setActions] = useState<DrawAction[]>([]);
  const [activeUsers, setActiveUsers] = useState<Set<string>>(new Set());

  const actionsCollectionRef = collection(db, 'canvasActions', canvasId, 'actions');

  // Listen to real-time canvas actions and snapshot
  useEffect(() => {
    console.log('🎨 Setting up real-time canvas listener...');

    // Listen to actions
    const actionsQuery = query(actionsCollectionRef, orderBy('timestamp', 'asc'), limit(1000));
    const unsubscribeActions = onSnapshot(actionsQuery, async (snapshot) => {
      const newActions: DrawAction[] = [];
      const users = new Set<string>();

      snapshot.forEach((doc) => {
        const data = doc.data();
        newActions.push({
          id: doc.id,
          ...data,
        } as DrawAction);
        users.add(data.userName);
      });

      setActions(newActions);
      setActiveUsers(users);
      // Note: redrawCanvas will be called by the separate useEffect below
    });

    return () => unsubscribeActions();
  }, [canvasId]);

  // Redraw entire canvas from actions
  const redrawCanvas = useCallback((actionsToRender: DrawAction[]) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    // Filter out undone actions
    const undoneIds = new Set(
      actionsToRender
        .filter(a => a.type === 'undo' && a.undoActionId)
        .map(a => a.undoActionId!)
    );

    const validActions = actionsToRender.filter(a =>
      a.type !== 'undo' && !undoneIds.has(a.id)
    );

    // Apply all valid actions
    validActions.forEach(action => {
      if (action.type === 'clear') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
      } else if (action.type === 'draw' || action.type === 'erase') {
        if (action.points && action.points.length > 0) {
          ctx.strokeStyle = action.type === 'erase' ? '#ffffff' : (action.color || '#000000');
          ctx.lineWidth = action.lineWidth || 3;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';

          ctx.beginPath();
          ctx.moveTo(action.points[0].x, action.points[0].y);

          for (let i = 1; i < action.points.length; i++) {
            ctx.lineTo(action.points[i].x, action.points[i].y);
          }

          ctx.stroke();
        }
      } else if (action.type === 'fill') {
        if (action.fillX !== undefined && action.fillY !== undefined && action.fillColor) {
          floodFill(ctx, action.fillX, action.fillY, action.fillColor);
        }
      }
    });
  }, [width, height]);

  // Redraw when actions change (but not while actively drawing)
  useEffect(() => {
    if (!isDrawing && actions.length >= 0) {
      redrawCanvas(actions);
    }
  }, [actions, isDrawing, redrawCanvas]);

  // Redraw when canvas ref or actions change (for orientation changes)
  useEffect(() => {
    if (canvasRef.current && actions.length > 0) {
      // Small delay to ensure canvas is rendered
      const timer = setTimeout(() => {
        redrawCanvas(actions);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [canvasRef.current]);

  // Flood fill algorithm (optimized)
  const floodFill = (ctx: CanvasRenderingContext2D, startX: number, startY: number, fillColor: string) => {
    const imageData = ctx.getImageData(0, 0, width, height);
    const pixels = imageData.data;

    const x = Math.floor(startX);
    const y = Math.floor(startY);

    // Check bounds
    if (x < 0 || x >= width || y < 0 || y >= height) return;

    const startPos = (y * width + x) * 4;
    const startR = pixels[startPos];
    const startG = pixels[startPos + 1];
    const startB = pixels[startPos + 2];

    // Convert fill color to RGB
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d')!;
    tempCtx.fillStyle = fillColor;
    tempCtx.fillRect(0, 0, 1, 1);
    const fillData = tempCtx.getImageData(0, 0, 1, 1).data;
    const fillR = fillData[0];
    const fillG = fillData[1];
    const fillB = fillData[2];

    // If clicking on same color, return
    const tolerance = 5;
    if (
      Math.abs(startR - fillR) <= tolerance &&
      Math.abs(startG - fillG) <= tolerance &&
      Math.abs(startB - fillB) <= tolerance
    ) {
      return;
    }

    // Use a queue for BFS approach
    const queue: { x: number; y: number }[] = [{ x, y }];
    const visited = new Set<string>();
    visited.add(`${x},${y}`);

    let pixelsFilled = 0;
    const maxPixels = width * height; // Allow full canvas flood fill

    while (queue.length > 0 && pixelsFilled < maxPixels) {
      const { x: cx, y: cy } = queue.shift()!;

      const pos = (cy * width + cx) * 4;

      // Check if this pixel matches the target color
      if (
        Math.abs(pixels[pos] - startR) <= tolerance &&
        Math.abs(pixels[pos + 1] - startG) <= tolerance &&
        Math.abs(pixels[pos + 2] - startB) <= tolerance
      ) {
        // Fill the pixel
        pixels[pos] = fillR;
        pixels[pos + 1] = fillG;
        pixels[pos + 2] = fillB;
        pixels[pos + 3] = 255;
        pixelsFilled++;

        // Add neighbors to queue
        const neighbors = [
          { x: cx + 1, y: cy },
          { x: cx - 1, y: cy },
          { x: cx, y: cy + 1 },
          { x: cx, y: cy - 1 },
        ];

        for (const neighbor of neighbors) {
          const key = `${neighbor.x},${neighbor.y}`;
          if (
            neighbor.x >= 0 &&
            neighbor.x < width &&
            neighbor.y >= 0 &&
            neighbor.y < height &&
            !visited.has(key)
          ) {
            visited.add(key);
            queue.push(neighbor);
          }
        }
      }
    }

    ctx.putImageData(imageData, 0, 0);
  };


  // Save action to Firestore
  const saveAction = async (action: Omit<DrawAction, 'id' | 'timestamp'>) => {
    try {
      await addDoc(actionsCollectionRef, {
        ...action,
        timestamp: serverTimestamp(),
      });
    } catch (error) {
      console.error('❌ Error saving action:', error);
    }
  };

  // Get canvas coordinates from event
  const getCanvasCoordinates = (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let clientX: number;
    let clientY: number;

    if ('touches' in e) {
      if (e.touches.length === 0) return null;
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  // Mouse/Touch handlers
  const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const coords = getCanvasCoordinates(e);
    if (!coords) return;

    if (currentTool === 'fill') {
      // Execute fill immediately
      saveAction({
        type: 'fill',
        userId,
        userName,
        fillX: coords.x,
        fillY: coords.y,
        fillColor: currentColor,
      });
    } else {
      setIsDrawing(true);
      setCurrentPath([coords]);
    }
  };

  const handleMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || currentTool === 'fill') return;

    e.preventDefault();
    const coords = getCanvasCoordinates(e);
    if (!coords) return;

    setCurrentPath(prev => [...prev, coords]);

    // Draw preview on canvas
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (currentPath.length > 0) {
      ctx.strokeStyle = currentTool === 'erase' ? '#ffffff' : currentColor;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      ctx.beginPath();
      ctx.moveTo(currentPath[currentPath.length - 1].x, currentPath[currentPath.length - 1].y);
      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();
    }
  };

  const handleEnd = () => {
    if (!isDrawing) return;
    setIsDrawing(false);

    if (currentPath.length > 1) {
      saveAction({
        type: currentTool === 'erase' ? 'erase' : 'draw',
        userId,
        userName,
        points: currentPath,
        color: currentColor,
        lineWidth,
      });
    }

    setCurrentPath([]);
  };

  // Clear canvas - delete all actions from Firestore
  const clearCanvas = async () => {
    console.log('🗑️ Clearing canvas...');
    try {
      const snapshot = await getDocs(actionsCollectionRef);
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      console.log('✅ All canvas actions cleared');
    } catch (error) {
      console.error('❌ Error clearing canvas actions:', error);
    }
  };

  // Undo last action by this user
  const undoLastAction = async () => {
    console.log('↩️ Attempting to undo last action...');

    // Find last action by this user that hasn't been undone
    const undoneIds = new Set(
      actions
        .filter(a => a.type === 'undo' && a.undoActionId)
        .map(a => a.undoActionId!)
    );

    const userActions = actions
      .filter(a => a.userId === userId && a.type !== 'undo' && !undoneIds.has(a.id))
      .reverse();

    console.log(`Found ${userActions.length} actions to potentially undo`);

    if (userActions.length > 0) {
      const lastAction = userActions[0];
      console.log('Undoing action:', lastAction.type, lastAction.id);

      await saveAction({
        type: 'undo',
        userId,
        userName,
        undoActionId: lastAction.id,
      });

      console.log('✅ Undo action saved');
    } else {
      console.log('❌ No actions to undo');
    }
  };

  // Clear all actions (admin only)
  const clearAllActions = async () => {
    try {
      const snapshot = await getDocs(actionsCollectionRef);
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      console.log('✅ All canvas actions cleared');
    } catch (error) {
      console.error('❌ Error clearing canvas actions:', error);
    }
  };

  return {
    canvasRef,
    currentTool,
    setCurrentTool,
    currentColor,
    setCurrentColor,
    lineWidth,
    setLineWidth,
    isDrawing,
    activeUsers,
    actions,
    handleStart,
    handleMove,
    handleEnd,
    clearCanvas,
    undoLastAction,
    clearAllActions,
  };
};
